import { useState } from "react";
import MainLayout from "./MainLayout";

const PricingCard = ({
  plan,
  price,
  subPrice,
  features,
  note,
  isFeatured = false,
}) => {
  const cardClasses = `p-6 rounded-2xl border ${
    isFeatured
      ? "border-purple-500 bg-purple-500/10"
      : "border-white/20 bg-gray-900/50"
  }`;

  const buttonClasses = `w-full py-3 rounded-lg font-semibold transition ${
    isFeatured
      ? "bg-purple-500 hover:bg-purple-600 text-white"
      : "bg-gray-700 hover:bg-gray-600 text-white"
  }`;

  return (
    <div className={cardClasses}>
      <h3 className="text-2xl font-bold text-white">{plan}</h3>

      <p className="mt-4 text-2xl font-extrabold text-white">{price}</p>

      {subPrice && (
        <div className="mt-4 flex items-center gap-2 text-sm">
          <span className="line-through text-gray-400">
            {subPrice.original}
          </span>
          <span className="rounded-full bg-green-500/20 px-2 py-1 text-xs font-semibold text-green-400">
            {subPrice.discount}
          </span>
        </div>
      )}
      <button className={`${buttonClasses} mt-10`}>
        {price === "Contact sales" ? "Contact Sales" : "Get Started"}
      </button>

      <ul className="mt-8 space-y-4 text-gray-300 text-left">
        {features.map((feature, index) => (
          <li key={index} className="flex gap-3">
            <span className="text-green-400">✔</span>
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      {note && <p className="mt-4 text-xs text-gray-400">{note}</p>}

    </div>
  );
};

const Pricing = ({ isInDashboard = false }) => {
  const [billingCycle, setBillingCycle] = useState("monthly");

  const pricingPlans = [
    {
      plan: "Free",
      monthly: {
        price: "₹0 / month",
        subPrice: { original: "₹1,999", discount: "Save 100%" },
      },
      yearly: {
        price: "₹0 / yr",
        subPrice: { original: "₹19,999", discount: "Save 100%" },
      },
      features: [
        "Access to all models",
        "Access to insights",
        "Ready dashboards",
        "2 files upload monthly",
        "5 prompts monthly",
        "File size up to 5 MB",
        "Download your work",
      ],
    },
    {
      plan: "Basic Individual",
      monthly: {
        price: "₹999 / month",
        subPrice: { original: "₹1,999", discount: "Save 50%" },
      },
      yearly: {
        price: "₹9,999 / yr",
        subPrice: { original: "₹19,999", discount: "Save 50%" },
      },
      features: [
        "Access to all models",
        "Access to insights",
        "Ready dashboards",
        "5 files upload daily",
        "15 prompts monthly",
        "File size up to 25 MB",
        "Download your work",
        "Save your work – 7 days",
      ],
    },
    {
      plan: "Pro Power Users",
      isFeatured: true,
      monthly: {
        price: "₹2,499 / month",
        subPrice: { original: "₹4,999", discount: "Save 50%" },
      },
      yearly: {
        price: "₹24,999 / yr",
        subPrice: { original: "₹49,999", discount: "Save 50%" },
      },
      features: [
        "Access to all models",
        "Access to insights",
        "Advanced reasoning",
        "Ready dashboards",
        "10 files upload daily",
        "File size up to 25 MB",
        "Download your work",
        "Save your work – 30 days",
        "30 prompts daily",
        "900 prompts monthly",
      ],
    },
    {
      plan: "Business Team",
      isFeatured: false,
      monthly: {
        price: "₹14,999 / month",
        subPrice: { original: "₹29,999", discount: "Save 50%" },
      },
      yearly: {
        price: "₹1,49,999 / yr",
        subPrice: { original: "₹2,99,999", discount: "Save 50%" },
      },
      features: [
        "Unlimited access to all models",
        "Unlimited insights",
        "Advanced reasoning",
        "Unlimited file uploads",
        "File size up to 50 MB",
        "Save & download work",
        "Priority email support",
        "Permanent file storage",
        "180 prompts daily",
        "5,400 prompts monthly",
      ],
    },
    {
      plan: "ENTERPRISE",
      monthly: { price: "Contact sales" },
      yearly: { price: "Contact sales" },
      features: [
        "Custom usage limits",
        "Dedicated support",
        "Custom storage & SLA",
        "Security & compliance",
      ],
      note: "For large teams & custom requirements",
    },
  ];

  const content = (
    <main className={`w-full max-w-7xl mx-auto flex flex-col items-center text-center px-6 md:px-12 space-y-12 pb-24 ${isInDashboard ? "pt-10" : "pt-32"}`}>
        <div className="space-y-4">
          <h1 className="text-5xl md:text-7xl font-extrabold text-gray-200 leading-tight">
            Pricing Plans
          </h1>
          <p className="text-xl text-gray-400">
            Choose the plan that's right for your team.
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex items-center gap-2 rounded-full bg-gray-800 p-1">
          <button
            onClick={() => setBillingCycle("monthly")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              billingCycle === "monthly"
                ? "bg-purple-500 text-white"
                : "text-gray-400"
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle("yearly")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              billingCycle === "yearly"
                ? "bg-purple-500 text-white"
                : "text-gray-400"
            }`}
          >
            Yearly
          </button>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 w-full max-w-7xl pt-8">
          {pricingPlans.map((plan, index) => {
            const billing = plan[billingCycle];

            return (
              <PricingCard
                key={index}
                plan={plan.plan}
                price={billing.price}
                subPrice={billing.subPrice}
                features={plan.features}
                note={plan.note}
                isFeatured={plan.isFeatured}
              />
            );
          })}
        </div>
      </main>
  );

  return isInDashboard ? content : <MainLayout>{content}</MainLayout>;
};

export default Pricing;